import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Tag, Sliders, Info } from "lucide-react";

const CURRENCY_LABEL = {
  SAR: "ريال سعودي (SAR)",
  AED: "درهم إماراتي (AED)",
  QAR: "ريال قطري (QAR)",
  KWD: "دينار كويتي (KWD)",
  OMR: "ريال عماني (OMR)",
  BHD: "دينار بحريني (BHD)",
  USD: "دولار أمريكي (USD)",
};

const NON_CHARGEABLE = new Set(["KWD", "OMR", "BHD"]); // display only; charged as SAR at Stripe

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [supported, setSupported] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.plans || []);
      setSupported(data.supported_currencies || []);
    } catch (e) {
      toast.error("فشل تحميل الخطط");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin me-2" /> جاري التحميل…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-plans">
      <div className="flex items-center gap-3">
        <Tag className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">خطط الاشتراك</h1>
          <p className="text-sm text-muted-foreground">أسعار خطتي 6 أشهر و12 شهر — تسري فورًا بعد الحفظ</p>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 text-blue-900 text-xs p-3 leading-relaxed flex gap-2">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <b>كيف تعمل الأسعار:</b> السعر بالريال السعودي هو الأساس. باقي العملات تُحسب تلقائيًا من سعر الصرف — إلا إذا كتبت قيمة مخصصة في جدول <b>"أسعار مخصصة لكل عملة"</b>. اترك الحقل فارغًا للاحتساب التلقائي.
          <br />
          <b>KWD / OMR / BHD:</b> تُعرَض للعميل بالعملة المحلية لكن الدفع في Stripe يتم بالريال السعودي (قيود حساب Stripe الحالي).
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} supported={supported} onSaved={load} />
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan, supported, onSaved }) {
  const [sar, setSar] = useState(plan.sar_amount);
  const [overrides, setOverrides] = useState(() => {
    // Turn overrides dict into a keyed input map, blank strings for absent currencies
    const base = {};
    supported.forEach((c) => { base[c] = plan.overrides?.[c] != null ? String(plan.overrides[c]) : ""; });
    return base;
  });
  const [showOverrides, setShowOverrides] = useState(Object.values(plan.overrides || {}).some(Boolean));
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    if (!sar || Number(sar) <= 0) {
      toast.error("سعر الريال السعودي مطلوب وأكبر من صفر");
      return;
    }
    setSaving(true);
    try {
      const cleanedOverrides = {};
      for (const [k, v] of Object.entries(overrides)) {
        const trimmed = String(v ?? "").trim();
        if (trimmed && Number(trimmed) > 0) cleanedOverrides[k] = Number(trimmed);
      }
      await api.put(`/admin/plans/${plan.id}`, {
        sar_amount: Number(sar),
        overrides: cleanedOverrides,
      });
      toast.success(`تم حفظ خطة ${plan.label}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 space-y-5" data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">{plan.label}</h3>
        <span className="text-[11px] text-muted-foreground">
          {plan.sub_interval === "month" && plan.sub_interval_count === 6 && "كل 6 أشهر"}
          {plan.sub_interval === "year" && plan.sub_interval_count === 1 && "سنويًا"}
        </span>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`sar-${plan.id}`}>السعر بالريال السعودي (SAR)</Label>
        <Input
          id={`sar-${plan.id}`}
          type="number"
          min={1}
          value={sar}
          onChange={(e) => setSar(e.target.value)}
          className="text-lg font-bold"
          data-testid={`plan-${plan.id}-sar`}
        />
        <p className="text-[11px] text-muted-foreground">
          الأساس — باقي العملات تُحسب من هذه القيمة تلقائيًا.
        </p>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowOverrides((v) => !v)}
          className="text-sm font-semibold text-primary hover:underline flex items-center gap-1.5"
          data-testid={`plan-${plan.id}-toggle-overrides`}
        >
          <Sliders className="h-3.5 w-3.5" />
          {showOverrides ? "إخفاء" : "عرض"} أسعار مخصصة لكل عملة (اختياري)
        </button>

        {showOverrides && (
          <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 space-y-2">
            {supported.filter((c) => c !== "SAR").map((c) => (
              <div key={c} className="grid grid-cols-[1fr_120px] items-center gap-3">
                <Label htmlFor={`${plan.id}-${c}`} className="text-xs font-normal flex items-center gap-2">
                  {CURRENCY_LABEL[c] || c}
                  {NON_CHARGEABLE.has(c) && (
                    <span className="text-[9px] rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">عرض فقط</span>
                  )}
                </Label>
                <Input
                  id={`${plan.id}-${c}`}
                  type="number"
                  min={1}
                  placeholder="تلقائي"
                  value={overrides[c] || ""}
                  onChange={(e) => setOverrides((o) => ({ ...o, [c]: e.target.value }))}
                  className="text-sm"
                  data-testid={`plan-${plan.id}-override-${c}`}
                />
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground pt-1">
              اتركه فارغًا للاحتساب التلقائي من سعر الريال. القيمة المخصصة تُعرض بالضبط.
            </p>
          </div>
        )}
      </div>

      {/* Live pricing preview so admin sees what the visitor will see */}
      <div className="rounded-lg border border-border p-3 bg-background">
        <div className="text-[11px] font-bold text-muted-foreground mb-2">معاينة الأسعار</div>
        <div className="grid grid-cols-2 gap-1 text-xs">
          {supported.map((c) => {
            const overrideTrim = (overrides[c] || "").toString().trim();
            const overrideNum = Number(overrideTrim);
            const hasOverride = overrideTrim.length > 0 && overrideNum > 0;
            // Client-side auto-preview: for SAR use the live input value; for other currencies,
            // scale the resolved server pricing by the ratio of new SAR / old SAR so the preview
            // updates live as admin types a new SAR base.
            const currentSar = Number(sar) || plan.sar_amount || 0;
            const ratio = plan.sar_amount ? currentSar / plan.sar_amount : 1;
            const auto = c === "SAR"
              ? currentSar
              : Math.round((plan.pricing?.[c] || 0) * ratio);
            const shown = hasOverride ? overrideNum : auto;
            return (
              <div key={c} className="flex items-center justify-between">
                <span className="text-muted-foreground">{c}</span>
                <span className={`font-mono ${hasOverride ? "font-bold text-primary" : ""}`}>
                  {shown ? shown : "—"}{hasOverride && <span className="ms-1 text-[9px]">مخصص</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <Button onClick={onSave} disabled={saving} className="w-full" data-testid={`plan-${plan.id}-save`}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 me-2" /> حفظ</>}
      </Button>
    </Card>
  );
}
